/**
 * Serialization boundary between the visual editor's working model
 * (`ContextFlow*` — see `types/contextFlow.ts`) and the persisted model
 * (`ContextDefinition` — see `types/context.d.ts`).
 *
 * The two models are deliberately different: the editor stores ergonomic,
 * UI-shaped values (`label: string`, `FilterState`, `sortBy`/`sortDir`,
 * `enrichment` record) while the persisted definition stores
 * `LocalizedString`, `UnifiedFilters`, `SortOrder[]`, and `LevelSettings`.
 * Every save must go through `toContextDefinition` and every load through
 * `fromContextDefinition` — passing flow state straight into a context
 * payload is a type error (and would persist a malformed definition).
 */
import type {
  ContextApplicability,
  ContextDefinition,
  ContextLevel,
  ContextSegment,
  ContextSettings,
  LevelSettings,
  SortDirection,
} from 'types/context';
import type {
  FilterOperator,
  UnifiedFilters,
  PerspectiveFilter,
  RelationshipFilter,
  FilterGroup,
  FilterExpression,
  ArcFilter,
} from 'types/filters';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import type {
  ContextCoverDef,
  ContextFlowLevel,
  ContextFlowSegment,
  ContextFlowRoot,
  ContextFooterDef,
} from '../types/contextFlow';
import type { FilterState, SimpleFilterGroup } from '../types/filterState';
import type { PerspectiveFilterState } from '../components/molecules/PerspectiveFilter/PerspectiveFilterMolecule';
import { nextFilterId } from './ids';

// ── Id minting on read ──────────────────────────────────────────────────────
// Deserialize (persisted → editor) mints a stable id for any FilterExpression /
// FilterGroup / SortClause read without one, so legacy contexts (stored before
// this field existed) still key their editor lists stably. Serialize
// (editor → persisted) never mints — it only passes an existing `id` through
// unchanged, so an editor model that legitimately has no id (e.g. a fixture
// built by hand, never round-tripped through deserialize) does not spuriously
// acquire one on save.
function ensureFilterExpressionId(f: FilterExpression): FilterExpression {
  return f.id ? f : { ...f, id: nextFilterId() };
}

// ── Enrichment ⇄ LevelSettings ──────────────────────────────────────────────
// `lazyLoad` is the only enrichment field that persists to LevelSettings.
// The six visualization flags (includeStatus, includeFlow, etc.) are now
// consumer concerns (see EnrichmentOverrides). Legacy stored values are
// read-tolerated: any key absent from this map is silently ignored on load.
type BooleanLevelSettingKey = 'lazyLoad';

const ENRICHMENT_TO_SETTING: Record<string, BooleanLevelSettingKey> = {
  lazy_load: 'lazyLoad',
};

// Deterministic per-level accent colors, assigned by index on load.
const LEVEL_COLORS = ['#377FC4', '#0891b2', '#dc2626', '#f59e0b', '#7c3aed', '#059669'];

function asDir(dir: 'asc' | 'desc' | undefined): SortDirection {
  // SortDirection's runtime values are the literal strings 'asc' / 'desc'.
  return (dir === 'desc' ? 'desc' : 'asc') as SortDirection;
}

// ── Filters: FilterState (editor) ⇄ UnifiedFilters (persisted) ───────────────

// Converts the legacy `PerspectiveFilterState` editor shape (nested
// `assigneeFilter`) to the canonical `PerspectiveFilter` shape (flat
// `assignees`). Still reachable via the `perspectiveFilters` channel-input
// path (`filterStateToUnified` below) for a caller that sets `FilterState.
// perspectiveFilters` directly rather than authoring a `{property:'perspective'}`
// pseudo-row — mirrors `arcFiltersChannelInputAlsoHoisted` / `relationshipFilters`
// legacy-channel tolerance (EAINT-725 Phase 4b).
export function perspectiveStateToUnified(p: PerspectiveFilterState): PerspectiveFilter {
  const specific = p.assigneeFilter?.filterType === 'specific';
  return {
    perspectiveSystemName: p.perspectiveSystemName,
    // `includeInHierarchy` IS the operator: checked ("in the hierarchy") = 'in',
    // unchecked ("excluded") = 'not_in'. Do not hardcode 'in' — that silently
    // drops every unchecked perspective's exclusion on save.
    operator: p.includeInHierarchy ? 'in' : 'not_in',
    assignees: specific ? p.assigneeFilter?.assigneeIds ?? [] : [],
    ...(p.minCount !== undefined ? { minCount: p.minCount } : {}),
  };
}

/**
 * The inverse of `perspectiveStateToUnified` — canonical `PerspectiveFilter`
 * (persisted/composed shape) to the editor's `PerspectiveFilterState`. Used
 * by read-only display surfaces (`FilterProvenanceGroupMolecule`, EAINT-781)
 * that render a stored/composed layer's clauses as chips via
 * `FilterChipsDisplayMolecule`, which only understands the editor shape.
 */
export function perspectiveUnifiedToState(p: PerspectiveFilter): PerspectiveFilterState {
  return {
    perspectiveSystemName: p.perspectiveSystemName,
    includeInHierarchy: p.operator !== 'not_in',
    assigneeFilter: p.assignees?.length
      ? { filterType: 'specific', assigneeIds: p.assignees }
      : { filterType: 'any' },
    ...(p.minCount !== undefined ? { minCount: p.minCount } : {}),
  };
}

// ── Groups: SimpleFilterGroup (editor) ⇄ FilterGroup (persisted) ────────────
// The two models are the same tree: the canonical `FilterGroupLeaf` union at
// every depth, nested through `groups`. Only the leaf KEY differs (`filters`
// vs `expressions`), so these two functions are a rename, not a translation —
// a lane condition inside a group round-trips as `{kind, filter}` untouched
// (SC7; the pseudo-property re-wrapping shim these replaced is deleted).
const PERSPECTIVE_PROPERTY_KEY = 'perspective';
const RELATIONSHIP_PROPERTY_KEY = 'relationship';

/**
 * Editor group → canonical group. Serialize never mints (see "Id minting on
 * read" above): a group or leaf that legitimately has no id keeps none, so a
 * hand-built fixture does not acquire one on save. Render-boundary callers
 * that need every node keyed normalise with `withStableFilterGroupIds` on
 * their own state BEFORE converting — that is where an id has to be minted,
 * because minting inside a derived conversion would hand the node a new React
 * key on every derivation.
 */
export function simpleGroupToUnified(g: SimpleFilterGroup): FilterGroup {
  const out: FilterGroup = {
    ...(g.id ? { id: g.id } : {}),
    operator: g.operator,
    expressions: g.filters ?? [],
  };
  if (g.groups && g.groups.length > 0) {
    out.groups = g.groups.map(simpleGroupToUnified);
  }
  return out;
}

/**
 * Canonical group → editor group. Mints on read (deserialize) so a legacy
 * context stored before `id` existed still keys its editor lists stably:
 * the group itself, and every PROPERTY leaf. A lane leaf is left alone — its
 * identity lives one level down on `leaf.filter.id`, and `withStableFilterGroupIds`
 * is the single place that normalises it, so minting here too would mean two
 * boundaries competing to invent the same leaf's id.
 */
export function unifiedGroupToSimple(g: FilterGroup): SimpleFilterGroup {
  const out: SimpleFilterGroup = {
    id: g.id ?? nextFilterId(),
    operator: g.operator,
    filters: (g.expressions ?? []).map((leaf) =>
      isPropertyLeaf(leaf) ? ensureFilterExpressionId(leaf) : leaf
    ),
  };
  if (g.groups && g.groups.length > 0) {
    out.groups = g.groups.map(unifiedGroupToSimple);
  }
  return out;
}

// ── Arc / relationship / perspective-as-a-property (top-level only) ⇄ canonical lane arrays ──
// Arc, relationship, and perspective are authored in the UI as property-picker
// rows (`{property:'arc'|'relationship'|'perspective', value:<ArcFilter|
// RelationshipFilter|PerspectiveFilter>}`) in the TOP-LEVEL `propertyFilters`
// channel, but MUST persist to their canonical `arcFilters[]` /
// `relationshipFilters[]` / `perspectiveFilters[]` lanes: the execution engines
// and `validateApplicability` only understand the array form for a top-level
// condition. A stray `{property:'arc'|'relationship'|'perspective'}` left in
// top-level `propertyFilters` would either silently match nothing (a level
// filter that never filters) or widen applicability to universal (a
// never-widen hole in the server validator). Hoist top-level rows into their
// lane on save, and render the lane back as top-level property rows on load
// (SC7 — surfaces an existing top-level lane instead of hiding it now that the
// pinned lane rows are gone; arc/relationship EAINT-725 Phase 3, perspective
// Phase 4b). A lane condition inside a `filterGroups` entry is a different
// case — see "Groups" above — it is never hoisted, and it is not a
// pseudo-property at all: it round-trips as a canonical `{kind, filter}` leaf
// in place (SC7).
const ARC_PROPERTY_KEY = 'arc';

function isArcPropertyRow(f: FilterExpression): boolean {
  return f?.property === ARC_PROPERTY_KEY;
}

// Carries the row's own id down onto the extracted payload when the payload
// doesn't already have one, so the lane filter keeps one identity across the
// top-level (non-group) hoist path.
function withRowId<T extends { id?: string }>(f: FilterExpression, payload: T): T {
  return f.id && payload.id === undefined ? { ...payload, id: f.id } : payload;
}

function extractArcFilter(f: FilterExpression): ArcFilter | null {
  const v = (f as { value?: unknown }).value;
  return v != null && typeof v === 'object' ? withRowId(f, v as ArcFilter) : null;
}

function isRelationshipPropertyRow(f: FilterExpression): boolean {
  return f?.property === RELATIONSHIP_PROPERTY_KEY;
}

function extractRelationshipFilter(f: FilterExpression): RelationshipFilter | null {
  const v = (f as { value?: unknown }).value;
  return v != null && typeof v === 'object' ? withRowId(f, v as RelationshipFilter) : null;
}

function isPerspectivePropertyRow(f: FilterExpression): boolean {
  return f?.property === PERSPECTIVE_PROPERTY_KEY;
}

function extractPerspectiveFilter(f: FilterExpression): PerspectiveFilter | null {
  const v = (f as { value?: unknown }).value;
  return v != null && typeof v === 'object' ? withRowId(f, v as PerspectiveFilter) : null;
}

/** Editor `FilterState` → persisted `UnifiedFilters`. */
export function filterStateToUnified(fs?: FilterState): UnifiedFilters {
  // Start from any lane filters already on their canonical arrays (e.g.
  // authored via a legacy C2 lane — `perspectiveFilters` is the legacy
  // `PerspectiveFilterState` editor shape, converted at this boundary), then
  // hoist top-level arc/relationship/perspective-as-property rows out of
  // propertyFilters into them.
  const arcFilters: ArcFilter[] = [...(fs?.arcFilters ?? [])];
  const relationshipFilters: RelationshipFilter[] = [...(fs?.relationshipFilters ?? [])];
  const perspectiveFilters: PerspectiveFilter[] = (fs?.perspectiveFilters ?? []).map(
    perspectiveStateToUnified
  );
  // Never-widen (SC6): a lane row is ALWAYS moved to its lane, even when its
  // value is malformed. Pushing the empty filter keeps the condition present and
  // fails closed at the engine (an unconfigured lane filter matches nothing);
  // dropping it would delete the condition and widen the level — the exact
  // direction SC6 forbids.
  const hoistLaneRow = (f: FilterExpression): boolean => {
    if (isArcPropertyRow(f)) {
      arcFilters.push(extractArcFilter(f) ?? ({} as ArcFilter));
      return false; // drop from top-level propertyFilters — moved to the lane
    }
    if (isRelationshipPropertyRow(f)) {
      relationshipFilters.push(extractRelationshipFilter(f) ?? ({} as RelationshipFilter));
      return false; // drop from top-level propertyFilters — moved to the lane
    }
    if (isPerspectivePropertyRow(f)) {
      perspectiveFilters.push(extractPerspectiveFilter(f) ?? ({} as PerspectiveFilter));
      return false; // drop from top-level propertyFilters — moved to the lane
    }
    return true;
  };

  const propertyFilters = (fs?.propertyFilters ?? []).filter(hoistLaneRow);

  return {
    propertyFilters,
    perspectiveFilters,
    relationshipFilters,
    arcFilters,
    filterGroups: (fs?.filterGroups ?? []).map(simpleGroupToUnified),
  };
}

/** Persisted `UnifiedFilters` → editor `FilterState`. */
export function unifiedToFilterState(uf?: UnifiedFilters): FilterState {
  // Render the canonical `arcFilters[]` / `relationshipFilters[]` /
  // `perspectiveFilters[]` lanes back as property-picker rows so the level
  // inspector shows them; the working model keeps them as property rows and
  // `filterStateToUnified` re-hoists them on save (round-trip stable).
  // Not minted: rebuilt fresh on every call (never itself persisted), so
  // minting here would hand a still-id-less lane filter a different id on
  // every derivation — the row's `id` rides on the payload's `id` instead. A
  // payload that predates this field is left for the consuming component to
  // normalize once (Phase 3).
  const arcRows: FilterExpression[] = (uf?.arcFilters ?? []).map(
    (a) => ({ id: a.id, property: ARC_PROPERTY_KEY, operator: 'equals', value: a } as FilterExpression)
  );
  const relationshipRows: FilterExpression[] = (uf?.relationshipFilters ?? []).map(
    (r) => ({ id: r.id, property: RELATIONSHIP_PROPERTY_KEY, operator: 'equals', value: r } as FilterExpression)
  );
  const perspectiveRows: FilterExpression[] = (uf?.perspectiveFilters ?? []).map(
    (p) => ({ id: p.id, property: PERSPECTIVE_PROPERTY_KEY, operator: 'equals', value: p } as FilterExpression)
  );
  return {
    propertyFilters: [
      ...(uf?.propertyFilters ?? []).map(ensureFilterExpressionId),
      ...arcRows,
      ...relationshipRows,
      ...perspectiveRows,
    ],
    perspectiveFilters: [],
    relationshipFilters: [],
    arcFilters: [],
    filterGroups: (uf?.filterGroups ?? []).map(unifiedGroupToSimple),
  };
}

// ── Applicability: ContextCoverDef fields ⇄ persisted ContextApplicability ───

/** Derive editable cover applicability state from a stored `ContextApplicability` value. */
export function deriveApplicability(
  applicability: ContextApplicability | null | undefined
): Pick<ContextCoverDef, 'applicabilityMode' | 'applicabilityFamilies' | 'applicabilityTypes' | 'applicabilityFilter'> {
  const pf = applicability?.propertyFilters ?? [];
  const persp = applicability?.perspectiveFilters ?? [];
  const groups = applicability?.filterGroups ?? [];
  if (pf.length === 0 && persp.length === 0 && groups.length === 0) {
    return { applicabilityMode: 'any' };
  }
  // "Specific types" shortcut: ONLY `label` (sphere-family) and/or `type`
  // (sphere-type) property filters — at most one of each, no perspective/groups.
  // Both axes drive the family/type picker; anything else is a custom filter.
  const labelFilters = pf.filter((f) => f.property === 'label');
  const typeFilters = pf.filter((f) => f.property === 'type');
  const isSpecific =
    persp.length === 0 &&
    groups.length === 0 &&
    pf.length > 0 &&
    labelFilters.length <= 1 &&
    typeFilters.length <= 1 &&
    labelFilters.length + typeFilters.length === pf.length;
  if (isSpecific) {
    const readValues = (f?: { values?: unknown; value?: unknown }): string[] => {
      if (Array.isArray(f?.values)) {
        return (f!.values as unknown[]).filter((v): v is string => typeof v === 'string');
      }
      if (f?.value != null) return [typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)];
      return [];
    };
    return {
      applicabilityMode: 'specific',
      applicabilityFamilies: readValues(labelFilters[0]),
      applicabilityTypes: readValues(typeFilters[0]),
    };
  }
  return {
    applicabilityMode: 'custom',
    applicabilityFilter: unifiedToFilterState(applicability ?? undefined),
  };
}

/** Build a stored `ContextApplicability` value from editable cover applicability state. */
export function buildApplicability(
  cover: Pick<ContextCoverDef, 'applicabilityMode' | 'applicabilityFamilies' | 'applicabilityTypes' | 'applicabilityFilter'>
): ContextApplicability | undefined {
  if (cover.applicabilityMode === 'specific') {
    const families = cover.applicabilityFamilies ?? [];
    const types = cover.applicabilityTypes ?? [];
    const propertyFilters = [
      ...(families.length > 0
        ? [{ property: 'label', operator: 'in' as FilterOperator, values: families }]
        : []),
      ...(types.length > 0
        ? [{ property: 'type', operator: 'in' as FilterOperator, values: types }]
        : []),
    ];
    if (propertyFilters.length === 0) return undefined;
    return { propertyFilters };
  }
  if (cover.applicabilityMode === 'custom' && cover.applicabilityFilter) {
    return filterStateToUnified(cover.applicabilityFilter);
  }
  return undefined;
}

// ── Segments ─────────────────────────────────────────────────────────────────

function flowSegmentToContextSegment(seg: ContextFlowSegment): ContextSegment {
  const out: ContextSegment = {
    id: seg.id,
    // seg.label is already a LocalizedValue ({ default, translations? }).
    label: { default: seg.label.default, ...(seg.label.translations ? { translations: seg.label.translations } : {}) },
    filters: filterStateToUnified(seg.filters),
  };
  if (seg.sortOverride) {
    out.sortOrder = [
      { property: seg.sortOverride.property, orderBy: asDir(seg.sortOverride.dir) },
    ];
  }
  // NOTE: ContextSegment has no per-segment limit field — `seg.pageSize` is dropped.
  return out;
}

function contextSegmentToFlow(cs: ContextSegment): ContextFlowSegment {
  const so = cs.sortOrder?.[0];
  return {
    id: cs.id,
    label: {
      default: cs.label?.default ?? '',
      ...(cs.label?.translations ? { translations: cs.label.translations } : {}),
    },
    filters: unifiedToFilterState(cs.filters),
    sortOverride: so
      ? { property: so.property, dir: String(so.orderBy) === 'desc' ? 'desc' : 'asc' }
      : null,
  };
}

// ── Levels ───────────────────────────────────────────────────────────────────

function enrichmentToSettings(
  enrichment?: Record<string, boolean>,
  pageSize?: number
): LevelSettings | undefined {
  const s: LevelSettings = {};
  for (const [key, settingKey] of Object.entries(ENRICHMENT_TO_SETTING)) {
    const v = enrichment?.[key];
    if (typeof v === 'boolean') s[settingKey] = v;
  }
  if (pageSize !== undefined) s.limit = pageSize;
  return Object.keys(s).length > 0 ? s : undefined;
}

function settingsToEnrichment(s?: LevelSettings): Record<string, boolean> {
  const e: Record<string, boolean> = {};
  for (const [key, settingKey] of Object.entries(ENRICHMENT_TO_SETTING)) {
    const v = s?.[settingKey];
    if (typeof v === 'boolean') e[key] = v;
  }
  return e;
}

function flowLevelToContextLevel(level: ContextFlowLevel, index: number): ContextLevel {
  // Type is just a `type` property filter — kept inline in `filters` so it is
  // visible and editable in the inspector's Filters section.
  const filters = filterStateToUnified(level.filters);
  const settings = enrichmentToSettings(level.enrichment, level.pageSize);
  const out: ContextLevel = {
    id: level.id,
    order: index,
    label: { default: level.label },
    direction: level.direction,
    repeat: level.repeat,
    filters,
  };
  if (level.icon) out.icon = level.icon;
  const sortClauses = (level.sort ?? []).filter((s) => s.property);
  if (sortClauses.length > 0) {
    out.sortOrder = sortClauses.map((s) => ({ id: s.id, property: s.property, orderBy: asDir(s.dir) }));
  }
  if (settings) out.settings = settings;
  if (level.segments && level.segments.length > 0) {
    out.segments = level.segments.map(flowSegmentToContextSegment);
  }
  return out;
}

function contextLevelToFlow(cl: ContextLevel, index: number): ContextFlowLevel {
  // The `type` property filter stays inline in `filters` — the inspector and
  // the flow node both read it from there.
  const filterState = unifiedToFilterState(cl.filters);
  const out: ContextFlowLevel = {
    id: cl.id,
    label: cl.label?.default ?? '',
    // 'both' is an ephemeral runtime override (the metaspace direction pill); it's
    // never persisted on a context level, so the editor only handles inwards/outwards.
    direction: cl.direction === 'both' ? 'outwards' : cl.direction,
    repeat: cl.repeat ?? 1,
    filters: filterState,
    enrichment: settingsToEnrichment(cl.settings),
    color: LEVEL_COLORS[index % LEVEL_COLORS.length],
  };
  if (cl.icon) out.icon = cl.icon;
  if (cl.sortOrder && cl.sortOrder.length > 0) {
    out.sort = cl.sortOrder.map((so) => ({
      id: so.id ?? nextFilterId(),
      property: so.property,
      dir: String(so.orderBy) === 'desc' ? 'desc' : 'asc',
    }));
  }
  if (cl.settings?.limit !== undefined) out.pageSize = cl.settings.limit;
  if (cl.segments && cl.segments.length > 0) {
    out.segments = cl.segments.map(contextSegmentToFlow);
  }
  return out;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Serialize the editor's working state into a persisted `ContextDefinition`.
 * `cacheable` / `cacheTTL` are intentionally omitted — they are protected,
 * migration-only settings (the schema-service strips them on write).
 */
export function toContextDefinition(
  root: ContextFlowRoot,
  levels: ContextFlowLevel[],
  footer: ContextFooterDef
): ContextDefinition {
  const settings: ContextSettings = {
    defaultLimit: footer.defaultLimit,
    maxDepth: footer.maxDepth,
    permissionCheck: footer.permissionCheck,
    includeAncestors: footer.includeAncestors,
  };
  return {
    root: {
      rootType: root.rootType,
      ...(root.sphereIds ? { sphereIds: root.sphereIds } : {}),
      ...(root.schemaType ? { schemaType: root.schemaType } : {}),
    },
    levels: levels.map((level, index) => flowLevelToContextLevel(level, index)),
    settings,
  };
}

/**
 * Deserialize a persisted `ContextDefinition` into the editor's working state.
 * Inverse of `toContextDefinition`.
 */
export function fromContextDefinition(definition: ContextDefinition): {
  root: ContextFlowRoot;
  levels: ContextFlowLevel[];
  footer: ContextFooterDef;
} {
  const s = definition.settings ?? ({} as ContextSettings);
  return {
    root: {
      rootType: definition.root?.rootType ?? 'sphere',
      sphereIds: definition.root?.sphereIds,
      schemaType: definition.root?.schemaType,
    },
    levels: (definition.levels ?? []).map((cl, index) => contextLevelToFlow(cl, index)),
    footer: {
      defaultLimit: s.defaultLimit ?? 50,
      maxDepth: s.maxDepth ?? 5,
      permissionCheck: s.permissionCheck ?? 'view',
      includeAncestors: s.includeAncestors ?? false,
      cacheTtlMin: s.cacheTTL != null ? Math.round(s.cacheTTL / 60) : undefined,
    },
  };
}
